import React, { useEffect, useRef } from 'react';
import TaskCard from './TaskCard';
import { UserPlus, Eye, Upload, Paperclip, MessageSquare, FileText } from 'lucide-react';

function TaskTable({
  title,
  tasks,
  getStatusColor,
  getTaskStatusDisplay,
  handleOpenSubmitModal,
  onViewStudentTaskDetail,
  showActions,
  showPoints,
  onEditTask,
  onDeleteTask,
  onViewTask,
  onAssignTask,
  onToggleVisibility,
  docenteView = false,
  highlightedTaskId = null
}) {
  const taskRefs = useRef({});

  useEffect(() => {
    if (highlightedTaskId && taskRefs.current[highlightedTaskId]) {
      taskRefs.current[highlightedTaskId].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedTaskId, tasks]);

  return (
    <div className="overflow-x-auto mb-8 p-5 bg-slate-800/40 border border-slate-700/50 rounded-2xl shadow-inner">
      {title && <h4 className="text-xl font-bold mb-4 text-white">{title} ({tasks.length})</h4>}
      {tasks.length === 0 ? (
        <p className="text-slate-400 text-sm">No hay tareas en esta categoría.</p>
      ) : (
        <>
          {/* Vista de tabla para pantallas grandes */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700/70">
              <thead className="bg-slate-800/80">
                <tr>
                  {!docenteView && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Cátedra</th>}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Tarea</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Plan / Unidad</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Descripción</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-300 uppercase">Puntos</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Vencimiento</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Estado</th>
                  {showPoints && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Calificación / Devolución</th>}
                  {(showActions || (!docenteView && onViewStudentTaskDetail)) && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Acciones</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {tasks.map((task) => {
                  const recursosCount = (docenteView ? task.recursos : task.TareaMaestra?.recursos)?.length || 0;
                  const entregasCount = Array.isArray(task.submission_path) ? task.submission_path.length : (task.submission_path ? 1 : 0);

                  return (
                    <tr
                      key={task.id}
                      ref={el => taskRefs.current[task.id] = el}
                      className={`hover:bg-slate-700/30 transition-colors ${highlightedTaskId === task.id ? 'bg-yellow-900/30 border-l-4 border-yellow-400' : ''}`}
                    >
                      {!docenteView && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-slate-200">
                          {task.TareaMaestra?.Catedra?.nombre} ({task.TareaMaestra?.Catedra?.anio})
                        </td>
                      )}
                      <td className="px-4 py-4 text-sm font-medium text-slate-200">
                        {docenteView ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); onViewTask(task); }}
                            className="text-blue-400 hover:text-blue-300 font-semibold text-left"
                          >
                            {task.titulo}
                          </button>
                        ) : (
                          <div>
                            <button
                              type="button"
                              onClick={() => onViewStudentTaskDetail && onViewStudentTaskDetail(task)}
                              className="text-white hover:text-purple-300 font-semibold text-left transition-colors"
                            >
                              {task.TareaMaestra?.titulo}
                            </button>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {recursosCount > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-blue-500/15 text-blue-300 border border-blue-500/30">
                                  <Paperclip size={11} /> {recursosCount} recurso(s)
                                </span>
                              )}
                              {entregasCount > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-purple-500/15 text-purple-300 border border-purple-500/30">
                                  <FileText size={11} /> {entregasCount} archivo(s)
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                        <div className="font-medium text-slate-200">
                          {docenteView
                            ? (task.UnidadPlan?.PlanDeClases?.titulo || 'General')
                            : (task.TareaMaestra?.UnidadPlan?.PlanDeClases?.titulo || 'General')}
                        </div>
                        <div className="text-xs text-slate-400">
                          {docenteView
                            ? (task.UnidadPlan?.periodo || 'Sin unidad')
                            : (task.TareaMaestra?.UnidadPlan?.periodo || 'Sin unidad')}
                        </div>
                      </td>
                      <td
                        className="px-4 py-4 text-sm text-slate-300 max-w-xs truncate"
                        dangerouslySetInnerHTML={{ __html: docenteView ? task.descripcion : task.TareaMaestra?.descripcion }}
                      />
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center font-semibold text-slate-200">
                        {docenteView ? task.puntos_posibles : task.TareaMaestra?.puntos_posibles} pts
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                        {(docenteView ? task.fecha_entrega : task.TareaMaestra?.fecha_entrega)
                          ? new Date(docenteView ? task.fecha_entrega : task.TareaMaestra.fecha_entrega).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(docenteView ? task : task.estado)}`}>
                          {getTaskStatusDisplay(task)}
                        </span>
                      </td>
                      {showPoints && (
                        <td className="px-4 py-4 text-sm">
                          <span className="font-bold text-emerald-400 text-base">
                            {task.puntos_obtenidos !== null && task.puntos_obtenidos !== undefined
                              ? `${task.puntos_obtenidos} / ${task.TareaMaestra?.puntos_posibles} pts`
                              : 'N/A'}
                          </span>
                          {task.comentario_docente && (
                            <p className="text-xs text-slate-300 mt-1 max-w-xs truncate flex items-center gap-1" title={task.comentario_docente}>
                              <MessageSquare size={12} className="text-emerald-400 flex-shrink-0" />
                              {task.comentario_docente}
                            </p>
                          )}
                        </td>
                      )}
                      {(showActions || (!docenteView && onViewStudentTaskDetail)) && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {!docenteView && onViewStudentTaskDetail && (
                              <button
                                onClick={() => onViewStudentTaskDetail(task)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700/80 hover:bg-slate-600 text-slate-200 text-xs font-medium transition-colors"
                              >
                                <Eye size={14} />
                                Detalle
                              </button>
                            )}
                            {showActions && (task.estado === 'ASIGNADA' || task.estado === 'VENCIDA') && (!task.submission_path || task.submission_path.length === 0) && !docenteView && (
                              <button
                                onClick={() => handleOpenSubmitModal(task)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors shadow-sm"
                              >
                                <Upload size={14} />
                                Entregar
                              </button>
                            )}
                            {docenteView && showActions && (
                              <div className="flex space-x-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); onEditTask(task); }}
                                  className="text-yellow-400 hover:text-yellow-500"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); onDeleteTask(task.id); }}
                                  className="text-red-400 hover:text-red-500"
                                >
                                  Eliminar
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAssignTask(task);
                                  }}
                                  className="text-blue-400 hover:text-blue-500 flex items-center gap-1"
                                >
                                  <UserPlus size={16} /> Asignar
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Vista de tarjetas para pantallas pequeñas */}
          <div className="block md:hidden space-y-4">
            {tasks.map((task) => {
              const taskCardProps = docenteView ? task : { ...task, TareaMaestra: task.TareaMaestra };

              return (
                <div
                  key={task.id}
                  ref={el => taskRefs.current[task.id] = el}
                  className={`${highlightedTaskId === task.id ? 'bg-yellow-800/50 border-l-4 border-yellow-400' : ''}`}
                >
                  <TaskCard
                    task={taskCardProps}
                    getStatusColor={getStatusColor}
                    getTaskStatusDisplay={getTaskStatusDisplay}
                    handleOpenSubmitModal={handleOpenSubmitModal}
                    onViewStudentTaskDetail={onViewStudentTaskDetail}
                    showActions={showActions}
                    showPoints={showPoints}
                    onEditTask={onEditTask}
                    onDeleteTask={onDeleteTask}
                    onViewTask={onViewTask}
                    onAssignTask={onAssignTask}
                    onToggleVisibility={onToggleVisibility}
                    docenteView={docenteView}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default TaskTable;
